import React from 'react';
import { marked } from 'marked';

type Token = any;

marked.setOptions({
  gfm: true,
  breaks: true
});

const renderInline = (tokens: Token[], keyPrefix: string): React.ReactNode[] =>
  tokens.map((token, index) => {
    const key = `${keyPrefix}-${index}`;
    switch (token.type) {
      case 'text':
        return token.text;
      case 'strong':
        return <strong key={key}>{renderInline(token.tokens ?? [], key)}</strong>;
      case 'em':
        return <em key={key}>{renderInline(token.tokens ?? [], key)}</em>;
      case 'codespan':
        return <code key={key}>{token.text}</code>;
      case 'br':
        return <br key={key} />;
      case 'link': {
        const href = typeof token.href === 'string' ? token.href : '';
        const safeHref = href.startsWith('https://') || href.startsWith('/') ? href : '';
        if (!safeHref) {
          return token.text;
        }
        return (
          <a key={key} href={safeHref} rel="noreferrer">
            {renderInline(token.tokens ?? [{ type: 'text', text: token.text }], key)}
          </a>
        );
      }
      default:
        return token.text || '';
    }
  });

const renderTokens = (tokens: Token[]): React.ReactNode[] =>
  tokens.map((token, index) => {
    const key = `block-${index}`;
    switch (token.type) {
      case 'heading':
        return React.createElement(
          `h${Math.min(token.depth ?? 3, 4)}`,
          { key },
          renderInline(token.tokens ?? [], key)
        );
      case 'paragraph':
        return <p key={key}>{renderInline(token.tokens ?? [], key)}</p>;
      case 'list':
        return token.ordered ? (
          <ol key={key}>
            {token.items?.map((item: Token, itemIndex: number) => (
              <li key={`${key}-item-${itemIndex}`}>{renderInline(item.tokens ?? [], key)}</li>
            ))}
          </ol>
        ) : (
          <ul key={key}>
            {token.items?.map((item: Token, itemIndex: number) => (
              <li key={`${key}-item-${itemIndex}`}>{renderInline(item.tokens ?? [], key)}</li>
            ))}
          </ul>
        );
      case 'code':
        return (
          <pre key={key}>
            <code>{token.text}</code>
          </pre>
        );
      case 'blockquote':
        return <blockquote key={key}>{renderTokens(token.tokens ?? [])}</blockquote>;
      case 'space':
        return null;
      case 'html':
        return null;
      default:
        if (token.tokens) {
          return <span key={key}>{renderInline(token.tokens, key)}</span>;
        }
        return token.text || null;
    }
  });

export const MarketingMarkdown = ({ content }: { content?: string }): JSX.Element => {
  if (!content) {
    return <></>;
  }

  const tokens = marked.lexer(content, { gfm: true, breaks: true });
  return <>{renderTokens(tokens)}</>;
};
